import { Breadcrumb } from "@twinkltech/curriculum-architect-component-library";
import {
  Curriculum,
  UnitPlan as UnitPlanType,
} from "@twinkltech/curriculum-architect-domain-hooks";
import { Text } from "@twinkltech/web-ui";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router-dom";

import { Tag } from "@/components//Tag/Tag";
import { TabLink } from "@/components/TabLink/TabLink";
import { TagList } from "@/components/TagList/TagList";

import { UnitInfo as UnitInfo } from "@/utils/getUnitFromCell/getUnitFromCell";

import { COMMON_NS } from "@/i18n/common/common.i18n";
import { paths } from "@/paths";

import { getThreadSlugTranslations } from "../TagList/utils/getThreadSlugTranslations/getThreadSlugTranslations";

import { SCHEME_HEADER_NS } from "./UnitHeader.i18n";

export const UnitHeader = ({
  curriculum,
  description,
  keyArt,
  subject,
  title,
  unitId,
  unitInfo,
}: {
  title?: string;
  description?: string;
  unitId: string;
  unitInfo?: UnitInfo | null;
  curriculum: Curriculum | null | undefined;
  subject?: UnitPlanType["subject"] | null;
  keyArt?: string;
}) => {
  const { t } = useTranslation(SCHEME_HEADER_NS);

  const cellId = unitInfo?.cell?.cellId || "generic";
  return (
    <>
      <Breadcrumb className="mb-6">
        <Breadcrumb.Item>
          <Link to={paths.CURRICULUM_LIST_PATH}>
            <span className="truncate max-w-[200px] block">
              {t("terms.my-curricula", {
                ns: COMMON_NS,
              })}
            </span>
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link
            to={generatePath(paths.CURRICULUM_SUBJECT_LIST_PATH, {
              curriculum_id: curriculum?.id || "",
            })}
          >
            <span className="truncate max-w-[200px] block">
              {curriculum?.displayLabel}
            </span>
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link
            to={generatePath(paths.SUBJECT_PLAN_EDIT_PATH, {
              curriculum_id: curriculum?.id || "",
              subject_id: subject?.subjectId || "",
            })}
          >
            {subject?.displayLabel}
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item current className="text-gray-500">
          <span className="truncate max-w-[200px] block">{title}</span>
        </Breadcrumb.Item>
      </Breadcrumb>
      <div
        aria-describedby="unit-description"
        aria-labelledby="unit-header-title"
        className="mb-10 flex w-full gap-300"
        role="banner"
      >
        <div className="w-[200px]">
          <img
            className="rounded border bg-neutral-100"
            role="presentation"
            src={keyArt}
          />
        </div>
        <div className="flex items-end justify-between w-full">
          <div className="max-w-[700px] flex flex-col gap-100">
            <Text as="h1" id="unit-header-title" variant="2xl-medium">
              {title}
            </Text>
            <Text variant="base-normal">
              {t("terms.curriculum", { ns: COMMON_NS })}:{" "}
              <Link
                className="text-blue-600 underline hover:text-blue-700 active:text-purple-600"
                to={generatePath(paths.CURRICULUM_SUBJECT_LIST_PATH, {
                  curriculum_id: curriculum?.id || "",
                })}
              >
                {curriculum?.displayLabel}
              </Link>
            </Text>
            <Text id="unit-description" variant="base-normal">
              {description}
            </Text>
            <TagList
              tags={
                new Set(
                  getThreadSlugTranslations(unitInfo?.resource.threads) || []
                )
              }
            />
          </div>
          <div className="flex gap-5 whitespace-nowrap">
            {unitInfo?.cell ? (
              <>
                <div className="flex">
                  <Tag className="self-end" variant="planned">
                    <Text as="span" variant="base-medium">
                      Planned
                    </Text>
                  </Tag>
                </div>
                <div className="flex flex-col gap-0.5">
                  <Text className="text-xs" variant="sm-medium">
                    {unitInfo?.cohortInfo?.displayLabel}
                  </Text>
                  <Text className="text-xs" variant="sm-medium">
                    {unitInfo?.startOnInfo?.displayLabel ===
                    unitInfo?.endOnInfo?.displayLabel
                      ? unitInfo?.startOnInfo?.displayLabel || ""
                      : `${unitInfo?.startOnInfo?.displayLabel} - ${unitInfo?.endOnInfo?.displayLabel}`}
                  </Text>
                </div>
              </>
            ) : (
              <Tag variant="not_yet_planned">Not planned</Tag>
            )}
          </div>
        </div>
      </div>
      <div className="flex justify-between items-center mb-10">
        <nav className="flex gap-3">
          <TabLink
            to={generatePath(paths.SCHEME_LEARNING_SEQUENCE_PATH, {
              curriculum_id: curriculum?.id || "",
              subject_id: subject?.subjectId || "",
              scheme_id: unitId,
              cell_id: cellId,
            })}
          >
            {t("tabs.learning-sequence")}
          </TabLink>
          <TabLink
            to={{
              pathname: generatePath(paths.SCHEME_PATH_SUPPORTING_MATERIALS, {
                curriculum_id: curriculum?.id || "",
                subject_id: subject?.subjectId || "",
                scheme_id: unitId,
                cell_id: cellId,
              }),
              search: window.location.search, // Preserve query parameters
            }}
          >
            {t("tabs.supporting-materials")}
          </TabLink>
        </nav>
      </div>
    </>
  );
};
